/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo} from 'react'
import {
    Box,
    SimpleGrid,
    Text,
    Center,
    useMultiStyleConfig,
    useCheckbox,
    VisuallyHidden,
    Flex
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {FILTER_LEGEND} from './refinements-utils'

const ColorRefinement = ({value, activeStyles, styles, selectedFilters, toggleFilter, filter}) => {
    const isSelected = useMemo(
        () => selectedFilters?.includes(value.value),
        [selectedFilters, value.value]
    )
    const {getCheckboxProps, getInputProps, getLabelProps, htmlProps} = useCheckbox({
        id: `${filter.attributeId}-${value.value}`,
        name: filter.attributeId,
        value: value.value,
        // note this doesn't use translation as it must match the label content which has hardcoded order
        'aria-label': `${value.label} ${value.hitCount}`,
        isChecked: isSelected,
        onChange: () => {
            toggleFilter(value, filter.attributeId, isSelected)
        }
    })

    const customColorMap = {
        brown: '#654321'
    }

    const swatchStyles = isSelected ? activeStyles : styles
    return (
        <Flex
            as="label"
            flexDirection="row"
            alignItems="center"
            gap={2}
            cursor="pointer"
            {...htmlProps}
        >
            <input {...getInputProps()} />
            <Flex
                {...getCheckboxProps()}
                alignItems="center"
                justifyContent="center"
                {...swatchStyles.swatch}
            >
                <Center
                    {...styles.swatchButton}
                    border={value.label.toLowerCase() === 'white' && '1px solid black'}
                >
                    <Box
                        height="100%"
                        width="100%"
                        minWidth="32px"
                        backgroundRepeat="no-repeat"
                        backgroundSize="cover"
                        backgroundColor={customColorMap[value.value] ?? value.value}
                        background={
                            // value.presentationId.toLowerCase() ===
                            //     'miscellaneous' &&
                            // cssColorGroups[value.presentationId.toLowerCase()]
                            true
                        }
                    />
                </Center>
            </Flex>
            <Text
                {...getLabelProps()}
                textStyle={isSelected ? 'Semi Bold Medium' : 'Regular Medium'}
                sx={{hyphens: 'auto'}}
                minWidth={0}
            >
                {value.label}
                <Text
                    as="span"
                    // visually hidden but passes the allyant check that input.aria-label matches the label content
                    fontSize="0"
                    lineHeight="1.3em"
                >
                    {
                        // without this space textContent concatenates the label with hitCount without space
                        ' '
                    }
                </Text>
                {/* moved inside the label as otherwise it breaks the allyant p2 check that input.aria-label matches the label content */}
                <Text
                    as="span"
                    backgroundColor="stokkeGray.bright"
                    borderRadius={5}
                    paddingY={0.5}
                    paddingX={1.5}
                    textStyle="Light Medium"
                    marginInlineStart={2}
                >
                    {value.hitCount}
                </Text>
            </Text>
        </Flex>
    )
}

ColorRefinement.propTypes = {
    value: PropTypes.object,
    activeStyles: PropTypes.object,
    styles: PropTypes.object,
    selectedFilters: PropTypes.array,
    toggleFilter: PropTypes.func,
    filter: PropTypes.object
}

const ColorRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'circle',
        disabled: false
    })

    const activeStyles = useMultiStyleConfig('SwatchGroup', {
        variant: 'circle',
        disabled: false,
        selected: true
    })

    return (
        <SimpleGrid as="fieldset" columns={{base: 2, lg: 3}} spacing={4} mt={1}>
            <VisuallyHidden as="legend">
                <FormattedMessage {...FILTER_LEGEND} values={filter} />
            </VisuallyHidden>
            {filter.values
                .filter((refinementValue) => refinementValue.hitCount > 0)
                .map((value, idx) => {
                    return (
                        <ColorRefinement
                            key={idx}
                            value={value}
                            activeStyles={activeStyles}
                            styles={styles}
                            selectedFilters={selectedFilters}
                            toggleFilter={toggleFilter}
                            filter={filter}
                        />
                    )
                })}
        </SimpleGrid>
    )
}

ColorRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array
}

export default ColorRefinements
